// HTTP client thin sobre fetch — inyecta el JWT desde Supabase en cada request
// y valida la response contra un schema Zod (typesafety end-to-end).
//
// Patrón:
//   const ctx = await apiGet('/api/contexto-lider/', contextoLiderSchema);
//   const reg = await apiPost('/api/registros/', body, registroProduccionHoraSchema);
//
// Lectura del token: usa `supabase.auth.getSession()` (NO el hook `useAuth`)
// porque estos helpers corren en `queryFn` / `mutationFn` de TanStack Query,
// fuera del React tree.

import type { ZodType } from 'zod';
import { supabase, API_URL } from '../supabase';
import { verComoTargetId } from '../ver-como';

/**
 * Header del modo "Ver como": si hay una suplantación activa, adjunta
 * `X-Ver-Como: <targetUserId>`. Se lee de sessionStorage (este módulo corre
 * fuera de React). El backend re-deriva el contexto como ese usuario y bloquea
 * toda escritura.
 */
function verComoHeader(): Record<string, string> {
  const id = verComoTargetId();
  return id ? { 'X-Ver-Como': id } : {};
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class AuthExpiredError extends ApiError {
  constructor() {
    super(401, 'session_expired', 'Su sesión expiró — vuelva a iniciar sesión');
  }
}

export class ForbiddenError extends ApiError {
  constructor(message: string, code = 'forbidden') {
    super(403, code, message);
  }
}

async function getAccessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) {
    throw new AuthExpiredError();
  }
  return data.session.access_token;
}

// Mensajes para el usuario final: los técnicos van a consola (soporte/devs),
// NUNCA a la pantalla. Los mensajes del backend (ya en español y pensados para
// humanos, ej. "No estás programado en esta línea hoy") se muestran tal cual.
const MSG_SERVIDOR =
  'El servidor tuvo un problema procesando la solicitud. Intente de nuevo; si persiste, contacte a soporte.';
const MSG_GENERICO = 'No pudimos completar la operación. Intente de nuevo.';
const MSG_DESINCRONIZADO =
  'No pudimos cargar la información. Recargue la página; si persiste, contacte a soporte.';

async function parseResponse<T>(res: Response, schema: ZodType<T>): Promise<T> {
  const body: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const errBody = (body ?? {}) as {
      error?: { code?: string; message?: string; details?: unknown };
    };
    const code = errBody.error?.code ?? `http_${res.status}`;
    // Sin mensaje del backend (proxy caído, HTML de error, etc.) → mensaje humano
    // y el detalle crudo a consola.
    let message = errBody.error?.message;
    if (!message) {
      message = res.status >= 500 ? MSG_SERVIDOR : MSG_GENERICO;
      console.error('[api]', res.status, res.url, body);
    }
    if (res.status === 401) throw new AuthExpiredError();
    if (res.status === 403) throw new ForbiddenError(message, code);
    throw new ApiError(res.status, code, message, errBody.error?.details);
  }

  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    // Backend cambió shape (típico: bundle viejo vs API nueva durante un deploy).
    // Error crítico como 500 sintético: detalle técnico a consola, mensaje humano
    // al usuario.
    console.error('[api] shape_mismatch en', res.url, parsed.error.flatten());
    throw new ApiError(500, 'shape_mismatch', MSG_DESINCRONIZADO, parsed.error.flatten());
  }
  return parsed.data;
}

/** Opciones extra de los helpers. `sinVerComo`: NO adjuntar el header de
 *  suplantación (para llamadas que hace el propio actor/directivo, ej. cargar
 *  el roster de "Ver como" o auditar el inicio, aun estando en modo espejo). */
export interface ApiOpts {
  sinVerComo?: boolean;
}

export async function apiGet<T>(
  path: string,
  schema: ZodType<T>,
  init?: RequestInit,
  opts?: ApiOpts,
): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(opts?.sinVerComo ? {} : verComoHeader()),
      ...(init?.headers ?? {}),
    },
  });
  return parseResponse(res, schema);
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  schema: ZodType<T>,
  init?: RequestInit,
  opts?: ApiOpts,
): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts?.sinVerComo ? {} : verComoHeader()),
      ...(init?.headers ?? {}),
    },
    body: JSON.stringify(body),
  });
  return parseResponse(res, schema);
}

export async function apiDelete(path: string, init?: RequestInit): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      ...verComoHeader(),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => null);
    const errBody = (body ?? {}) as { error?: { code?: string; message?: string } };
    const code = errBody.error?.code ?? `http_${res.status}`;
    let message = errBody.error?.message;
    if (!message) {
      message = res.status >= 500 ? MSG_SERVIDOR : MSG_GENERICO;
      console.error('[api]', res.status, res.url, body);
    }
    if (res.status === 401) throw new AuthExpiredError();
    if (res.status === 403) throw new ForbiddenError(message, code);
    throw new ApiError(res.status, code, message);
  }
}

export async function apiPatch<T>(
  path: string,
  body: unknown,
  schema: ZodType<T>,
  init?: RequestInit,
): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...verComoHeader(),
      ...(init?.headers ?? {}),
    },
    body: JSON.stringify(body),
  });
  return parseResponse(res, schema);
}

export async function apiPut<T>(
  path: string,
  body: unknown,
  schema: ZodType<T>,
  init?: RequestInit,
): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...verComoHeader(),
      ...(init?.headers ?? {}),
    },
    body: JSON.stringify(body),
  });
  return parseResponse(res, schema);
}
